import { api, get, post } from '@/lib/api';
import type {
  Artigo,
  ArtigoEncontrado,
  ArtigoListado,
  SituacaoDeArtigo,
  SugestaoDeArtigo,
  Transicao,
  VersaoDeArtigo,
  Visibilidade,
} from '@/features/knowledge/tipos';

/**
 * Acesso à base de conhecimento.
 *
 * Uma função por rota de `knowledge.controller.ts`, sem nenhuma inventada.
 *
 * ## Três leituras diferentes, e a tela não deve confundi-las
 *
 *   • **`GET /knowledge/articles`** sem `q` é a lista filtrada — a estante;
 *   • **`GET /knowledge/articles?q=`** é a busca de verdade: índice invertido
 *     em português, com radicalização e prefixo, e o recorte de público dentro
 *     da condição do índice. Ela funciona sob RLS, que é o que `ILIKE` não faz
 *     (docs/architecture/01 §F.9);
 *   • **`GET /tickets/:id/knowledge/suggestions`** é a busca do atendimento:
 *     mesma máquina, alimentada pelo texto do chamado e somada à identidade de
 *     serviço, item de configuração e categoria. Ninguém digita nada.
 *
 * A terceira é a que dá o retorno do ITIL, e é a que aparece sem ser pedida.
 */

const RAIZ = '/knowledge/articles';

const patch = <T>(caminho: string, corpo: unknown) =>
  api<T>(caminho, { method: 'PATCH', body: corpo });

const remover = (caminho: string) => api<void>(caminho, { method: 'DELETE' });

export interface FiltroDeArtigos {
  q?: string;
  status?: SituacaoDeArtigo;
  visibility?: Visibilidade;
  categoryId?: string;
  ownerId?: string;
  reviewDue?: boolean;
}

/**
 * Devolve `ArtigoListado[]` sem `q` e `ArtigoEncontrado[]` com `q` — as duas
 * formas compartilham o que a lista mostra, e a união é resolvida na tela.
 */
export function listarArtigos(filtro: FiltroDeArtigos = {}) {
  const busca = new URLSearchParams();
  if (filtro.q && filtro.q.trim() !== '') busca.append('q', filtro.q.trim());
  if (filtro.status) busca.append('status', filtro.status);
  if (filtro.visibility) busca.append('visibility', filtro.visibility);
  if (filtro.categoryId) busca.append('categoryId', filtro.categoryId);
  if (filtro.ownerId) busca.append('ownerId', filtro.ownerId);
  if (filtro.reviewDue) busca.append('reviewDue', 'true');
  const consulta = busca.toString();
  return get<Array<ArtigoListado & Partial<ArtigoEncontrado>>>(
    consulta ? `${RAIZ}?${consulta}` : RAIZ,
  );
}

export const obterArtigo = (id: string) => get<Artigo>(`${RAIZ}/${id}`);
export const listarVersoes = (id: string) => get<VersaoDeArtigo[]>(`${RAIZ}/${id}/versions`);

/** O grafo do ciclo de vida vem do servidor: a tela não o duplica. */
export const obterCicloDeVida = () =>
  get<{ transitions: Transicao[] }>(`${RAIZ}/lifecycle`);

export interface NovoArtigo {
  title: string;
  summary: string;
  bodyHtml: string;
  keywords?: string[];
  visibility?: Visibilidade;
  categoryId?: string;
  ownerId?: string;
  serviceIds?: string[];
  ciIds?: string[];
  reviewIntervalDays?: number;
  sourceTicketId?: string;
}

export const criarArtigo = (corpo: NovoArtigo) => post<Artigo>(RAIZ, corpo);

export interface EdicaoDeArtigo extends Partial<Omit<NovoArtigo, 'sourceTicketId'>> {
  changeNote?: string;
  version?: number;
}

export const editarArtigo = (id: string, corpo: EdicaoDeArtigo) =>
  patch<Artigo>(`${RAIZ}/${id}`, corpo);

export const transicionarArtigo = (
  id: string,
  corpo: { to: SituacaoDeArtigo; note?: string; version?: number },
) => post<Artigo>(`${RAIZ}/${id}/transition`, corpo);

export const restaurarVersao = (id: string, corpo: { number: number; changeNote?: string }) =>
  post<Artigo>(`${RAIZ}/${id}/versions/restore`, corpo);

export interface UsoEmChamado {
  ticketId: string;
  resolved?: boolean;
  helpful?: boolean;
  note?: string;
}

export interface ContadoresDeUtilidade {
  usageCount: number;
  resolvedCount: number;
  helpfulCount: number;
  notHelpfulCount: number;
}

/**
 * "Usei este artigo neste chamado."
 *
 * A resposta traz os contadores **relidos do banco**, depois da trigger: a tela
 * mostra o número que o banco calculou, não o que ela achava que ia dar.
 */
export const vincularAoChamado = (id: string, corpo: UsoEmChamado) =>
  post<{ link: unknown } & ContadoresDeUtilidade>(`${RAIZ}/${id}/tickets`, corpo);

export const desvincularDoChamado = (id: string, ticketId: string) =>
  remover(`${RAIZ}/${id}/tickets/${ticketId}`);

export const publicarErroConhecidoComoArtigo = (corpo: {
  knownErrorId: string;
  visibility?: Visibilidade;
}) => post<Artigo>(`${RAIZ}/from-known-error`, corpo);

// ── dentro do chamado ──────────────────────────────────────────────────────

export const sugerirParaChamado = (ticketId: string, limite = 5) =>
  get<SugestaoDeArtigo[]>(`/tickets/${ticketId}/knowledge/suggestions?limit=${limite}`);

export const conhecimentoDoChamado = (ticketId: string) =>
  get<{
    usados: Array<{
      ticketId: string;
      resolved: boolean;
      helpful: boolean | null;
      note: string | null;
      usedAt: string;
      article: {
        id: string;
        code: string;
        title: string;
        summary: string;
        status: SituacaoDeArtigo;
        visibility: Visibilidade;
      };
    }>;
    gerados: Array<{
      id: string;
      code: string;
      title: string;
      summary: string;
      status: SituacaoDeArtigo;
      visibility: Visibilidade;
    }>;
  }>(`/tickets/${ticketId}/knowledge`);

export const criarArtigoDoChamado = (ticketId: string, corpo: NovoArtigo) =>
  post<Artigo>(`/tickets/${ticketId}/knowledge`, corpo);

/** Chaves do cache, num lugar só — invalidar errado é o bug que ninguém vê. */
export const chavesDeConhecimento = {
  raiz: ['conhecimento'] as const,
  lista: (filtro: FiltroDeArtigos) => ['conhecimento', 'lista', filtro] as const,
  artigo: (id: string) => ['conhecimento', 'artigo', id] as const,
  versoes: (id: string) => ['conhecimento', 'versoes', id] as const,
  ciclo: ['conhecimento', 'ciclo'] as const,
  doChamado: (ticketId: string) => ['conhecimento', 'chamado', ticketId] as const,
  sugestoes: (ticketId: string) => ['conhecimento', 'sugestoes', ticketId] as const,
};
